import { LoadLayoutConfirmDialog } from '@features/dashboard/components/LoadLayoutConfirmDialog';
import { LoadLayoutDialog } from '@features/dashboard/components/LoadLayoutDialog';
import { SaveLayoutDialog } from '@features/dashboard/components/SaveLayoutDialog';
import { WidgetType } from '@features/dashboard/enums';
import { useLayoutSaveLoad } from '@features/dashboard/hooks/useLayoutSaveLoad';
import { WidgetPayload } from '@features/dashboard/types';
import { useWidgets } from '@features/dashboard/widgets/useWidgets';
import { useAppAuthentication } from '@hooks/useAppAuthentication';
import { useURLSearchParams } from '@hooks/useURLSearchParams';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import CandleStickChartIcon from '@mui/icons-material/CandlestickChart';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import EmailIcon from '@mui/icons-material/Email';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/Person';
import OrganizationIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import PowerIcon from '@mui/icons-material/Power';
import HelpIcon from '@mui/icons-material/QuestionMark';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SaveIcon from '@mui/icons-material/Save';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import TaskIcon from '@mui/icons-material/Task';
import ViewListIcon from '@mui/icons-material/ViewList';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import {
  Backdrop,
  Badge,
  Box,
  Button,
  ClickAwayListener,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Popper,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Stack,
  styled,
  Toolbar,
  Typography,
} from '@mui/material';
import { PermissionsEnum } from '@protos/user';
import { checkIsOutliersAdmin } from '@shared/utils/permissions';
import { Dashboard } from '@protos/v2/dashboard';
import { config, streamV2 } from '@services/context';
import { toastifyService } from '@services/ToastifyService';
import fluxAppLogoWhite from '@shared/assets/flux-app-logo-white.svg';
import ExpandableSearchLookup from '@shared/components/ExpandableSearchLookup';
import { FluxIconButton } from '@shared/components/FluxIconButton';
import { FluxIconMenu } from '@shared/components/FluxIconMenu';
import { FluxTextMenu } from '@shared/components/FluxTextMenu';
import { OrderNotificationsPanel } from '@shared/components/OrderNotifications/OrderNotificationsPanel';
import { useAppBarContext } from '@shared/contexts/AppBarContext';
import { useDashboardContext } from '@shared/contexts/DashboardContext';
import { useOrderNotifications } from '@shared/contexts/OrderNotificationsContexts';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { useUserProductsAndTenorsContext } from '@shared/contexts/UserProductsAndTenorsProvider';
import theme from '@shared/themes/darkTheme';
import {
  checkIsAdminReadOnlyUser,
  checkIsAdminUser,
  checkIsDTUser,
  checkIsFluxAdminUser,
  checkIsInternalUser,
  checkIsLadderUser,
  checkIsPremiumUser,
} from '@utils/permissions';
import { capitalizeFirstLetter, extractURLPageName } from '@utils/string';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { AppBarButton } from '../components/AppBarButton';
import { DemoLabel } from '../components/DemoLabel';
import { MigrationBanner, MIGRATION_BANNER_HEIGHT } from '../components/MigrationBanner';
import { allReferenceMenuOptions, MenuOption } from '../components/ReferenceMenuOptions';
import { ShareDashboardDialog } from '../components/ShareDashboardDialog';
import { ProductFinder } from '../product-finder/ProductFinder';
import { BadgeColor, DRAWER_WIDTH, NETWORK_COLOR, SUPPORT_NUMBERS } from './constants';
import { DashboardList } from './DashboardList';
import { useAutoClose } from './useAutoClose';
import { useDashboardList } from './useDashboardList';

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open',
})<AppBarProps>(({ theme }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        width: `calc(100% - ${DRAWER_WIDTH}px)`,
        marginLeft: `${DRAWER_WIDTH}px`,
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

type FluxAppBarProps = {
  children: ReactNode;
};

export const FluxAppBar = ({ children }: FluxAppBarProps) => {
  const user = useUserContext();
  const { userProducts } = useUserProductsAndTenorsContext();
  const { handleLogout } = useAppAuthentication();
  const navigate = useNavigate();
  const {
    updateDashboard,
    deleteDashboard,
    addNewWidgets,
    addDashboard,
    currentDashboard: selectedDashboard,
    allDashboards,
    setCurrentDashboardById,
    setNeedsFittingWidgets,
    loadDashboard,
    clearCurrentDashboard,
  } = useDashboardContext();
  const { onConfirmDeleteDashboard } = useDashboardList(
    updateDashboard,
    deleteDashboard,
    addDashboard,
    setCurrentDashboardById,
    navigate,
    allDashboards,
    selectedDashboard
  );
  const { menuItems, getWidgetIcon } = useWidgets();
  const currentUrl = window.location.href;
  const adminPageName = currentUrl.includes('admin') ? capitalizeFirstLetter(extractURLPageName(currentUrl)) : null;
  const { isAppbarOpen, setIsAppbarOpen } = useAppBarContext();
  const [isProductFinderOpen, setIsProductFinderOpen] = useState(false);
  const { countdownProgress, isLocked, toggleLock, startAutoCloseTimer, clearAutoCloseTimer } = useAutoClose(isAppbarOpen, setIsAppbarOpen);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const { notifications, selectedOrderTypes } = useOrderNotifications();
  const filteredNotifications = useMemo(() => {
    return notifications.filter(notification => selectedOrderTypes.includes(notification.order.order_type));
  }, [notifications, selectedOrderTypes]);
  const unreadCount = useMemo(() => {
    return filteredNotifications.filter(n => !n.read).length;
  }, [filteredNotifications]);
  const { resetURLSearchParams } = useURLSearchParams();
  const [isDemoLabelVisible, setIsDemoLabelVisible] = useState(true);

  const userInitials = useMemo(() => {
    if (!user?.full_name) return '';
    const nameParts = user.full_name.split(' ');
    return nameParts.length > 1 ? nameParts[0][0] + nameParts[nameParts.length - 1][0] : nameParts[0][0];
  }, [user?.full_name]);
  const networkStatus: BadgeColor = useMemo(() => NETWORK_COLOR[streamV2.status] as BadgeColor, [streamV2.status]);

  const createNumberMenu = (menuItems: { countryCode: string; phoneNumber: string }[]) => {
    return menuItems.map(item => ({
      Icon: <span>{item.countryCode}</span>,
      label: item.phoneNumber,
      subItems: [
        {
          Icon: <PhoneIcon fontSize="small" sx={{ fontSize: 15 }} />,
          label: 'Call',
          onMenuItemClick: () => window.open(`tel:${item.phoneNumber}`, '_blank'),
        },
        {
          Icon: <ContentCopyIcon fontSize="small" sx={{ fontSize: 15 }} />,
          label: 'Copy',
          onMenuItemClick: () => {
            navigator.clipboard.writeText(item.phoneNumber);
            toastifyService.showSuccess(`${item.phoneNumber} copied to clipboard`);
          },
        },
      ],
    }));
  };

  const allAdminMenuOptions: MenuOption[] = useMemo(
    () => [
      { name: 'Alerts', path: 'alerts', Icon: <ReportProblemIcon sx={{ fontSize: 15 }} />, disabled: !user?.is_flux_admin },
      {
        name: 'Settlements',
        path: 'settlements',
        Icon: <ViewListIcon sx={{ fontSize: 15 }} />,
        disabled: !user?.is_flux_admin && !user?.permissions.includes(PermissionsEnum.ONYX_USER),
      },
      {
        name: 'Trading',
        path: 'trading',
        Icon: <CandleStickChartIcon sx={{ fontSize: 15 }} />,
        disabled: !user?.is_flux_admin || !user?.is_trading_admin,
      },
      { name: 'Outliers', path: 'outliers', Icon: <AutoGraphIcon sx={{ fontSize: 15 }} />, disabled: !checkIsOutliersAdmin(user) },
      { name: 'Tasks', path: 'tasks', Icon: <TaskIcon sx={{ fontSize: 15 }} />, disabled: !user?.is_flux_admin },
      { name: 'Endpoint Measures', path: 'endpoint-measures', Icon: <EqualizerIcon sx={{ fontSize: 15 }} />, disabled: !user?.is_flux_admin },
      { name: 'Live Websockets', path: 'live-websockets', Icon: <PowerIcon sx={{ fontSize: 15 }} />, disabled: !user?.is_flux_admin },
      { name: 'Freemium', path: 'freemium', Icon: <MoneyOffIcon sx={{ fontSize: 15 }} />, disabled: !user?.is_flux_admin },
    ],
    [user]
  );

  const allSelecteDashboardWidgets = useMemo(() => selectedDashboard?.json?.widgets || [], [selectedDashboard]);
  const hasTradingBlotterWidget = useMemo(
    () => allSelecteDashboardWidgets.some(widget => widget.type === WidgetType.TradingBlotter),
    [allSelecteDashboardWidgets]
  );
  const filteredMenuItems = useMemo(
    () =>
      menuItems
        .filter(item => {
          if (item.type === WidgetType.TradingBlotter && hasTradingBlotterWidget) return false;
          return true;
        })
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map(item => ({
          ...item,
          Icon: getWidgetIcon(item.type),
        })),
    [menuItems, hasTradingBlotterWidget]
  );
  const allowedOCLTrading = checkIsLadderUser(user) && !!user?.ladder_limits?.length;

  const isAllowedUserAccess = checkIsAdminUser(user) || checkIsAdminReadOnlyUser(user) || checkIsFluxAdminUser(user);
  const isDTUser = checkIsDTUser(user);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dashboardToDelete, setDashboardToDelete] = useState<Dashboard | null>(null);
  const [dashboardToShare, setDashboardToShare] = useState<Dashboard | null>(null);
  const [isSpeedDialOpen, setIsSpeedDialOpen] = useState(false);

  const onConfirmAddWidget = useCallback(
    async (type: WidgetType, defaultPayload?: WidgetPayload) => {
      addNewWidgets([{ id: uuidv4(), type, payload: defaultPayload }]);
    },
    [addNewWidgets]
  );

  const {
    isSaveDialogOpen,
    isLoadDialogOpen,
    isConfirmDialogOpen,
    selectedLayout,
    openSaveDialog,
    openLoadDialog,
    closeSaveDialog,
    closeLoadDialog,
    handleSaveLayout,
    handleLoadLayout,
    handleConfirmLoad,
    handleCancelLoad,
  } = useLayoutSaveLoad(selectedDashboard, loadDashboard);

  const isPremiumUser = checkIsPremiumUser(user);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <MigrationBanner />
      {config.isUAT && <DemoLabel isDemoLabelVisible={isDemoLabelVisible} setIsDemoLabelVisible={setIsDemoLabelVisible} />}

      <AppBar position="fixed" open={isAppbarOpen} sx={{ top: `${MIGRATION_BANNER_HEIGHT}px` }}>
        <Toolbar style={{ minHeight: 45, maxHeight: 45, paddingLeft: 10, paddingRight: 17 }}>
          <IconButton
            color="inherit"
            aria-label="Open Sidebar"
            onClick={() => setIsAppbarOpen(true)}
            edge="start"
            sx={[
              {
                mr: 2,
                fontSize: 20,
                color: !config.isDev ? (config.isUAT ? theme.palette.background.uatBanner : 'none') : theme.palette.primary.main,
              },
              isAppbarOpen && { display: 'none' },
            ]}
          >
            <MenuIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Box display="flex" alignItems="center" gap={1}>
            {selectedDashboard && isPremiumUser && (
              <>
                <Typography variant="h6">{selectedDashboard.name}</Typography>
                <FluxTextMenu
                  label="+ Add New Widget"
                  menuList={filteredMenuItems.map(item => ({
                    Icon: item.Icon,
                    label: item.displayName,
                    onMenuItemClick: () => onConfirmAddWidget(item.type, item.defaultPayload),
                  }))}
                />
                <FluxTextMenu
                  label="Layout"
                  menuList={[
                    {
                      Icon: <SaveIcon />,
                      label: 'Save Layout',
                      onMenuItemClick: () => openSaveDialog(),
                    },
                    {
                      Icon: <SaveAltIcon />,
                      label: 'Load Layout',
                      onMenuItemClick: () => openLoadDialog(),
                    },
                    {
                      Icon: <AspectRatioIcon />,
                      label: 'Fit widgets',
                      onMenuItemClick: () => setNeedsFittingWidgets(true),
                    },
                  ]}
                />
              </>
            )}
            {adminPageName && <Typography variant="h6">{`Admin - ${adminPageName}`}</Typography>}
          </Box>
          <Box display="flex" marginLeft="auto" alignItems="center">
            {isPremiumUser && (
              <>
                <Box position="relative" sx={{ marginRight: 2, marginTop: '1px' }}>
                  <ExpandableSearchLookup
                    tooltip="QuickSearch"
                    items={userProducts.map(product => ({ id: product.id.toString(), name: product.full_name, secondaryName: product.short_name }))}
                  />
                </Box>
                <FluxIconButton Icon={ManageSearchIcon} label="Detailed Search" onClick={() => setIsProductFinderOpen(true)} />
              </>
            )}

            <FluxIconMenu
              tooltip="Help"
              CustomAvatar={<HelpIcon fontSize="small" sx={{ fontSize: 12 }} />}
              menuList={
                isPremiumUser
                  ? [
                      {
                        Icon: <EmailIcon fontSize="small" sx={{ fontSize: 15 }} />,
                        label: 'Email',
                        onMenuItemClick: () => window.open('mailto:support@flux.live', '_blank'),
                      },
                      {
                        Icon: <PhoneIcon fontSize="small" sx={{ fontSize: 15 }} />,
                        label: 'Phone Numbers',
                        subItems: createNumberMenu(SUPPORT_NUMBERS),
                      },
                      {
                        Icon: <SearchIcon fontSize="small" sx={{ fontSize: 15 }} />,
                        label: 'Lookup Symbols',
                        onMenuItemClick: () =>
                          window.open(
                            'https://docs.google.com/spreadsheets/d/1sqwqaFzOXnNnl4g6U4K9UGwAj70St-6eIZTbECr3RK8/edit?gid=0#gid=0',
                            '_blank'
                          ),
                      },
                    ]
                  : [
                      {
                        Icon: <EmailIcon fontSize="small" sx={{ fontSize: 15 }} />,
                        label: 'Email',
                        onMenuItemClick: () => window.open('mailto:support@flux.live', '_blank'),
                      },
                    ]
              }
              width={25}
              height={25}
              footer={
                <Box sx={{ px: 2, py: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                    {config.env.ENV ?? 'local'} | {config.env.BUILD_ID ?? 'dev'}
                  </Typography>
                </Box>
              }
            />
            <Badge
              color={networkStatus}
              variant="dot"
              overlap="circular"
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
            >
              <FluxIconMenu
                tooltip="Account"
                width={25}
                height={25}
                menuList={[
                  {
                    Icon: <SettingsIcon sx={{ fontSize: 15 }} fontSize="small" />,
                    label: 'Manage Account',
                    onMenuItemClick: () => {
                      clearCurrentDashboard();
                      navigate('/manage-account');
                    },
                  },
                  {
                    Icon: <LogoutIcon sx={{ fontSize: 15 }} fontSize="small" color="error" />,
                    label: 'Logout',
                    onMenuItemClick: () => handleLogout(),
                  },
                ]}
                CustomAvatar={userInitials ? <Typography fontSize={12}>{userInitials}</Typography> : <AccountCircleIcon fontSize="small" />}
              />
            </Badge>
            {(allowedOCLTrading || isDTUser) && (
              <IconButton color="inherit" onClick={() => setIsNotificationsOpen(true)} sx={{ mr: 1 }}>
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}
          </Box>
        </Toolbar>
        <Drawer
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              top: `${MIGRATION_BANNER_HEIGHT}px`,
              height: `calc(100% - ${MIGRATION_BANNER_HEIGHT}px)`,
            },
          }}
          variant="persistent"
          anchor="left"
          open={isAppbarOpen}
          onMouseEnter={clearAutoCloseTimer}
          onMouseLeave={startAutoCloseTimer}
        >
          <DrawerHeader style={{ minHeight: 45, maxHeight: 45 }}>
            <Box component="img" src={fluxAppLogoWhite} alt="Flux Logo" sx={{ height: 18, marginRight: 'auto' }} />
            <Box display="flex" alignItems="center" gap={2}>
              {!isLocked && countdownProgress > 0 && (
                <LinearProgress
                  variant="determinate"
                  value={countdownProgress}
                  sx={{
                    width: 50,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                />
              )}
              <FluxIconButton
                Icon={isLocked ? LockIcon : LockOpenIcon}
                label={isLocked ? 'Unlock Sidebar' : 'Lock Sidebar'}
                onClick={toggleLock}
                sx={{ fontSize: 20, padding: 0 }}
              />
              <FluxIconButton
                Icon={ArrowCircleLeftIcon}
                label="Close Sidebar"
                onClick={() => setIsAppbarOpen(false)}
                sx={{ fontSize: 20, padding: 0 }}
              />
            </Box>
          </DrawerHeader>
          <Divider />
          {config.isDev && (
            <Box display="flex" justifyContent="center" alignContent="center" sx={{ backgroundColor: theme.palette.primary.main }}>
              <Typography variant="caption" sx={{ fontSize: 10, marginRight: 1, color: 'white' }}>
                Development Environment
              </Typography>
            </Box>
          )}
          {config.isUAT && (
            <Box display="flex" justifyContent="center" alignContent="center" sx={{ backgroundColor: theme.palette.background.uatBanner }}>
              <Typography variant="caption" sx={{ fontSize: 10, marginRight: 1, color: 'white' }}>
                UAT Environment
              </Typography>
            </Box>
          )}
          <DashboardList
            dashboardToDelete={dashboardToDelete}
            setDashboardToDelete={setDashboardToDelete}
            setAnchorEl={setAnchorEl}
            setDashboardToShare={setDashboardToShare}
          />
          {isPremiumUser && (
            <>
              <Divider />
              <List>
                {allReferenceMenuOptions.map(menuOption => (
                  <AppBarButton key={`appbar-${menuOption.name}`} name={menuOption.name} path={menuOption.path} icon={menuOption.Icon} />
                ))}
              </List>

              {allowedOCLTrading && !!user?.ladder_limits?.length && (
                <>
                  <Divider />
                  <List>
                    <AppBarButton name="OCL Trading" path="/ocl-trading" icon={<CurrencyExchangeIcon sx={{ fontSize: 15 }} />} />
                  </List>
                </>
              )}
              {isAllowedUserAccess && (
                <>
                  <Divider />
                  <List>
                    <AppBarButton name="Users" path="/users" icon={<AccountCircleIcon sx={{ fontSize: 15 }} />} />
                    <AppBarButton name="Organizations" path="/organizations" icon={<OrganizationIcon sx={{ fontSize: 15 }} />} />
                  </List>
                </>
              )}
              {checkIsInternalUser(user) && (
                <>
                  <Divider />
                  <List>
                    <AppBarButton name="Trader Marks" path="/trader-marks" icon={<PowerIcon sx={{ fontSize: 15 }} />} />
                  </List>
                </>
              )}
              {checkIsOutliersAdmin(user) && (
                <>
                  <Divider />
                  <Box display="flex" justifyContent="space-between" alignItems="center" marginTop={1} height={10}>
                    <Typography variant="caption" sx={{ fontSize: 10, marginLeft: 1 }}>
                      ADMIN MENU
                    </Typography>
                  </Box>

                  <List>
                    {allAdminMenuOptions
                      .filter(item => !item.disabled)
                      .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
                      .map(adminMenuOption => (
                        <AppBarButton
                          key={`appbar-${adminMenuOption.name}`}
                          name={adminMenuOption.name}
                          path={`/admin/${adminMenuOption.path}`}
                          icon={adminMenuOption.Icon}
                        />
                      ))}
                  </List>
                  <Divider />
                </>
              )}
            </>
          )}
          <Box display="flex" justifyContent="center" marginTop="auto" alignContent="center">
            <Typography variant="caption" sx={{ fontSize: 9, textAlign: 'center' }}>
              © {new Date().getFullYear()} Onyx Hub. All Rights Reserved.
            </Typography>
          </Box>
        </Drawer>
        <Divider />
        <Stack
          maxHeight={`calc(97vh - ${MIGRATION_BANNER_HEIGHT}px)`}
          minHeight={`calc(97vh - ${MIGRATION_BANNER_HEIGHT}px)`}
          overflow="auto"
          width="100%"
          paddingBottom={1}
        >
          {children}
        </Stack>
      </AppBar>
      {currentUrl.includes('dashboard') && (
        <>
          <Backdrop open={isSpeedDialOpen} sx={{ color: '#fff', zIndex: theme => theme.zIndex.appBar + 1 }} />
          <SpeedDial
            ariaLabel="Add Widget"
            FabProps={{ size: 'small' }}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 16,
              zIndex: theme => theme.zIndex.appBar + 1,
              '& .MuiSpeedDial-fab': {
                bgcolor: theme.palette.primary.main,
                right: '-40px ',
                '&:hover': {
                  bgcolor: theme.palette.background.buyHover,
                },
              },
              '& .MuiSpeedDialAction-fab': {
                minWidth: 150,
                borderRadius: 0,
              },
            }}
            icon={<SpeedDialIcon />}
            onClose={() => setIsSpeedDialOpen(false)}
            onOpen={() => setIsSpeedDialOpen(true)}
            open={isSpeedDialOpen}
            direction="up"
          >
            {filteredMenuItems.map(widget => (
              <SpeedDialAction
                key={widget.type}
                icon={
                  <Box display="flex" alignItems="center" flex={1} gap={2} paddingLeft={1} paddingRight={1}>
                    <Typography sx={{ fontSize: 10, marginRight: 'auto' }}>{widget.displayName}</Typography>
                    {widget.Icon}
                  </Box>
                }
                onClick={() => onConfirmAddWidget(widget.type, widget.defaultPayload)}
              />
            ))}
          </SpeedDial>
        </>
      )}
      <ClickAwayListener
        onClickAway={() => {
          setAnchorEl(null);
          setDashboardToDelete(null);
        }}
      >
        <Popper
          id={anchorEl ? 'delete-dashboard-popper' : undefined}
          open={!!anchorEl}
          anchorEl={anchorEl}
          placement="right-start"
          sx={{
            zIndex: 10000,
          }}
        >
          <Stack sx={{ p: 1, bgcolor: 'background.paper', marginTop: 1, gap: 1 }}>
            <Stack>
              <Typography sx={{ fontSize: 11 }}>Delete dashboard - {dashboardToDelete?.name}?</Typography>
              <Typography variant="caption" sx={{ fontSize: 10 }}>
                This action cannot be undone.
              </Typography>
            </Stack>

            <Box display="flex" flex={1} justifyContent="end" gap={1} paddingLeft={12}>
              <Button variant="outlined" size="small" sx={{ fontSize: 9 }} onClick={() => setAnchorEl(null)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                sx={{
                  fontSize: 9,
                  backgroundColor: theme.palette.background.sell,
                  '&:hover': { bgcolor: `${theme.palette.background.sellHover} !important`, color: `white !important` },
                }}
                onClick={() => {
                  if (dashboardToDelete) onConfirmDeleteDashboard(dashboardToDelete.id);
                  setAnchorEl(null);
                  setDashboardToDelete(null);
                }}
              >
                Delete
              </Button>
            </Box>
          </Stack>
        </Popper>
      </ClickAwayListener>
      <ProductFinder open={isProductFinderOpen} onClose={() => setIsProductFinderOpen(false)} />
      <OrderNotificationsPanel open={isNotificationsOpen} onClose={() => setIsNotificationsOpen(false)} isDTUser={isDTUser} />
      <ShareDashboardDialog
        isOpen={!!dashboardToShare}
        dashboard={dashboardToShare}
        onClose={() => {
          setDashboardToShare(null);
          resetURLSearchParams({});
        }}
      />

      <SaveLayoutDialog open={isSaveDialogOpen} onClose={closeSaveDialog} onSave={handleSaveLayout} currentDashboard={selectedDashboard!} />
      <LoadLayoutDialog open={isLoadDialogOpen} onClose={closeLoadDialog} onLoad={handleLoadLayout} currentDashboard={selectedDashboard!} />
      <LoadLayoutConfirmDialog open={isConfirmDialogOpen} onClose={handleCancelLoad} onConfirm={handleConfirmLoad} selectedLayout={selectedLayout} />
    </Box>
  );
};
